import {
  primaryFontFamily,
  styled,
  useDimension,
} from '@openeducation/oe-rn-themes';
import type { DefaultTFuncReturn } from 'i18next';
import React from 'react';
import { Platform, StyleProp, ViewStyle } from 'react-native';
import { Divider } from 'react-native-paper';

import { PaginatedHeaderArrowButton } from './paginated-header-arrow-button';
import MDText from '../../atoms/md-text/md-text';
import { ml, mr } from '../../style-utils';

export interface PaginatedPanelProps {
  prevButtonLabel?: DefaultTFuncReturn;
  nextButtonLabel?: DefaultTFuncReturn;
  totalPages: number;
  currentPage: number;
  title?: string;
  style?: StyleProp<ViewStyle>;
  onChange?: (page: number) => void;
  children?: React.ReactNode;
}

export const PaginatedPanel: React.FC<PaginatedPanelProps> = ({
  prevButtonLabel,
  nextButtonLabel,
  totalPages,
  currentPage,
  title,
  children,
  style,
  onChange,
}) => {
  const { isMediumSize } = useDimension();
  const isWebPlatform = Platform.OS === 'web';
  const canGoNext = currentPage !== null && currentPage < totalPages;
  const canGoBack = currentPage !== null && currentPage > 0;

  return (
    <Container style={style}>
      <Header>
        <PaginatedHeaderArrowButton
          onPress={() => onChange?.(currentPage - 1)}
          disabled={!canGoBack}
          style={mr(24)}
          iconLeft="chevron-left"
        >
          {prevButtonLabel as unknown as string}
        </PaginatedHeaderArrowButton>

        {!isMediumSize && <VerticalDivider />}
        <MDText
          size={16}
          fontFamily={primaryFontFamily}
          renderWithinText={isWebPlatform}
        >
          {(title ?? '').toUpperCase()}
        </MDText>
        {!isMediumSize && <VerticalDivider />}

        <PaginatedHeaderArrowButton
          onPress={() => onChange?.(currentPage + 1)}
          disabled={!canGoNext}
          style={[ml(24)]}
          iconRight="chevron-right"
        >
          {nextButtonLabel as unknown as string}
        </PaginatedHeaderArrowButton>
      </Header>
      {children}
    </Container>
  );
};
const VerticalDivider = styled(Divider)`
  width: 1px;
  height: 35px;
`;
const Header = styled.View`
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 16px;
  min-height: 24px;
`;

const Container = styled.View<{ minHeight?: number }>`
  border-color: ${({ theme }) => theme.color.border};
  border-width: 1px;
  padding: 16px;
  border-radius: 8px;
  ${({ minHeight }) => (minHeight ? `min-height:${minHeight}px;` : '')}
`;

export default PaginatedPanel;
